import { PRIVACY_CLASSNAMES } from "@/lib/analytics/shared";
import { PencilIcon } from "@heroicons/react/outline";
import { classNames } from "lib/utils";
import React, { useEffect, useState } from "react";

export interface EditableStringProps extends React.ComponentProps<"input"> {
  value: string;
  setValue?: (value: string) => void | boolean | Promise<boolean> | Promise<void>;
  placeholder?: string;
  hideIcon?: boolean;
  immediateFocus?: boolean;
  className?: string;
}

const EditableString = ({
  value,
  setValue,
  placeholder,
  hideIcon,
  immediateFocus,
  className,
  ...rest
}: EditableStringProps) => {
  const [displayedValue, setDisplayedValue] = useState(value);
  const [editing, setEditing] = useState(immediateFocus ? true : false);
  const editable = Boolean(setValue);
  useEffect(() => {
    setDisplayedValue(value);
  }, [value]);

  const handleSetValue = () => {
    const result = setValue?.(displayedValue);

    if (result === false) {
      // Return to the original value
      setDisplayedValue(value);
    } else if (result instanceof Promise) {
      // Optimistically set the new value
      setDisplayedValue(displayedValue);

      result.then((success) => {
        if (success === false) {
          // Return to the original value if the promise resolves false
          setDisplayedValue(value);
        }
      });
    } else {
      // This was a synchronous call and it succeeded, so set the value
      setDisplayedValue(displayedValue);
    }
  };

  if (editing) {
    return (
      <input
        autoFocus
        value={displayedValue}
        placeholder={placeholder}
        onChange={(event) => {
          setDisplayedValue(event.target.value);
        }}
        onBlur={() => {
          if (displayedValue !== value) {
            handleSetValue();
          }
          setEditing(false);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            handleSetValue();
            setEditing(false);
          }
          if (event.key === "Escape") {
            setDisplayedValue(value);
            setEditing(false);
            event.stopPropagation();
            event.preventDefault();
          }
        }}
        {...rest}
        className={classNames(
          "w-full truncate rounded bg-oldgray-200 px-8 py-4 focus:outline-none",
          className,
          !hideIcon && "mr-24",
        )}
        style={{ fontWeight: "unset", fontSize: "unset" }}
      />
    );
  }
  return (
    <span
      className={classNames(
        "group inline-flex min-w-[40px] gap-8 truncate whitespace-nowrap rounded px-8 py-4",
        editable && "cursor-pointer hover:bg-oldgray-200",
        !displayedValue && "text-oldgray-500",
        className,
      )}
      onClick={() => editable && setEditing(true)}
    >
      <span className="min-h-[1lh] w-full flex-initial truncate">{displayedValue || placeholder}</span>
      {!hideIcon && editable && (
        <div className="flex shrink-0 items-center opacity-50 group-hover:opacity-100">
          <PencilIcon className="h-16 w-16 text-oldgray-600" />
        </div>
      )}
    </span>
  );
};

export default EditableString;
